import React, { useRef, useState } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { AutoCompleteKeyword } from "@/model";
import { useDialog } from "@/hook";
import { useUpdateAutocompleteKeyword } from "@/query/autocompleteKeyword";
import { errorToast, successToast } from "@/component/Toast";
import { Popover, PopoverButton, PopoverItem, PopoverMenu } from "@/component/Popover";
import HighlightText from "@/component/HighlightText";
import { DeleteIcon, EditIcon } from "@/assets/svgs/icons";


interface AutocompleteKeywordFragmentProps {
    autocompleteKeyword: AutoCompleteKeyword;
    selectedIds: string[];
    searchKeyword?: string;
    onCheck: (e: React.ChangeEvent<HTMLInputElement>, autocompleteKeyword: AutoCompleteKeyword) => void;
    onDelete: () => Promise<void>;
}

const AutocompleteKeywordFragment: React.FC<AutocompleteKeywordFragmentProps> = (props) => {
    const { autocompleteKeyword, selectedIds, searchKeyword, onCheck, onDelete } = props;

    const intl = useIntl();
    const dialog = useDialog();

    const inputRef = useRef<HTMLInputElement>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);

    const { mutateAsync: updateAutocompleteKeyword, isPending: isUpdating } = useUpdateAutocompleteKeyword(
        autocompleteKeyword.agent_id,
        autocompleteKeyword.autocomplete_id,
        autocompleteKeyword.id
    );

    const onClickUpdate = () => {
        setIsEditing(true);
        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    };

    const onClickDelete = () => {
        dialog.open({
            title: intl.formatMessage({ id: "i08025" }),
            content: intl.formatMessage({ id: "i08026" }),
            onConfirm: onDelete,
            confirmText: intl.formatMessage({ id: "b0008" }),
            variant: "danger"
        });
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if(!inputRef.current?.value) {
            setValidated(true);
            return;
        }
        await updateAutocompleteKeyword({ keyword: inputRef.current.value })
            .then(() => {
                successToast(intl.formatMessage({ id: "t003" }));
                setIsEditing(false);
                setValidated(false);
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t004" }));
            });
    };

    return (
        <div role="row"
             className="selection-table-tr group"
        >
            <div role="cell"
                 className="selection-table-td"
            >
                <input type='checkbox'
                       checked={selectedIds.includes(autocompleteKeyword.id)}
                       className={classNames(
                           selectedIds.length ? "opacity-100" : "opacity-0",
                           "selection-table-checkbox group-interaction"
                        )}
                        onChange={e => onCheck(e, autocompleteKeyword)}
                />
            </div>
            <div role="cell"
                 className="selection-table-td"
            >
                {isEditing ? (
                    <form className="w-full selection-table-form" onSubmit={onSubmit}>
                        <input type="text"
                               ref={inputRef}
                               disabled={isUpdating}
                               className={classNames(validated && "input-invalid")}
                               defaultValue={autocompleteKeyword.keyword}
                               onBlur={() => setIsEditing(false)}
                        />
                    </form>
                ) : (
                    <HighlightText query={autocompleteKeyword.keyword}
                                   keyword={searchKeyword}
                                   highlightClassName="text-highlight"
                    />
                )}
            </div>
            <div role="cell"
                 className="selection-table-td"
            >
                <Popover>
                    {({ open, close }) => (
                        <>
                            <PopoverButton className="selection-table-popover-btn"
                                           open={open}
                                           close={close}
                            />
                            <PopoverMenu>
                                <PopoverItem onClick={onClickUpdate}
                                            className="selection-table-popover-item"
                                >
                                    <EditIcon className="size-3.5" />
                                    {intl.formatMessage({ id: "i08024" })}
                                </PopoverItem>
                                <PopoverItem onClick={onClickDelete}
                                            className="selection-table-popover-item text-point-red"
                                >
                                    <DeleteIcon className="size-3.5" />
                                    {intl.formatMessage({ id: "i08025" })}
                                </PopoverItem>
                            </PopoverMenu>
                        </>
                    )}
                </Popover>
            </div>
        </div>
    );
};

export default AutocompleteKeywordFragment;